<script setup lang="ts">
import { ChevronRight, KeyRound, Plus, SearchX } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { toast } from "vue-sonner";
import { upsertRole, useRoles } from "@/api/admin";
import AdminNav from "@/components/admin/AdminNav.vue";
import { accessTabs } from "@/components/admin/accessTabs";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import FormError from "@/components/common/FormError.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";

definePage({ meta: { title: "Access", requires: { adminArea: "access" } } });

const router = useRouter();
const queryClient = useQueryClient();
const { can } = useSession();
const roles = useRoles();
const q = ref("");
const list = computed(() => {
  const needle = q.value.trim().toLowerCase();
  return (roles.data.value ?? []).filter((r) => !needle || [r.id, r.label, r.description].some((s) => s.toLowerCase().includes(needle)) || r.grants.some((g) => g.capability.includes(needle)));
});

const open = ref(false);
const newId = ref("");
const newLabel = ref("");
const newDescription = ref("");
const busy = ref(false);
const error = ref<unknown>(null);
const idValid = computed(() => /^[a-z][a-z0-9-]*$/.test(newId.value));

function slugFromLabel() {
  if (!newId.value) newId.value = newLabel.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").replace(/^[^a-z]+/, "");
}

async function create() {
  if (!idValid.value || !newLabel.value.trim()) return;
  busy.value = true;
  error.value = null;
  try {
    await upsertRole(newId.value, { label: newLabel.value.trim(), description: newDescription.value.trim() });
    await queryClient.invalidateQueries({ queryKey: ["admin", "roles"] });
    toast.success(`Role ${newLabel.value} created. Add grants next.`);
    open.value = false;
    await router.push(`/admin/access/roles/${newId.value}`);
  } catch (err) {
    error.value = err;
  } finally {
    busy.value = false;
  }
}

const tabs = computed(() => accessTabs(can));
</script>

<template>
  <div>
    <PageHeader :crumbs="[{ label: t.admin.title, to: '/admin' }]" :title="t.nav.access" description="Who may do what. Roles bundle grants; bindings give roles to people and groups.">
      <template #actions>
        <Button @click="(open = true), (newId = ''), (newLabel = ''), (newDescription = ''), (error = null)"><Plus /> {{ t.admin.newRole }}</Button>
      </template>
      <template #tabs><AdminNav :items="tabs" :label="t.nav.access" /></template>
    </PageHeader>

    <FilterBar v-model:search="q" placeholder="Search roles or capabilities…" label="Filter roles" class="mb-5" />
    <ListSkeleton v-if="roles.isPending.value" :rows="5" />
    <ErrorState v-else-if="roles.error.value" :error="roles.error.value" @retry="roles.refetch()" />
    <EmptyState v-else-if="list.length === 0" :icon="q ? SearchX : KeyRound" :title="q ? t.common.noResults : 'No roles yet'" />
    <ul v-else class="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
      <li v-for="r in list" :key="r.id" class="min-w-0">
        <Card as-child variant="interactive" padding="sm" class="group h-full">
          <RouterLink :to="`/admin/access/roles/${r.id}`">
            <div class="flex min-w-0 items-center gap-2">
              <span class="min-w-0 truncate font-medium">{{ r.label }}</span>
              <span class="min-w-0 truncate font-mono text-xs text-muted-foreground">{{ r.id }}</span>
              <Badge v-if="r.builtin" variant="muted">built-in</Badge>
              <ChevronRight class="ml-auto size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
            </div>
            <p class="line-clamp-2 text-sm text-muted-foreground">{{ r.description || "No description." }}</p>
            <p class="mt-auto text-xs text-muted-foreground">
              {{ r.grants.length }} grant{{ r.grants.length === 1 ? "" : "s" }} · {{ r.bindings.length }} binding{{ r.bindings.length === 1 ? "" : "s" }}
              <template v-if="r.bindings.some((b) => b.subjectType === 'everyone')"> · everyone</template>
            </p>
          </RouterLink>
        </Card>
      </li>
    </ul>

    <Dialog v-model:open="open">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{{ t.admin.newRole }}</DialogTitle>
          <DialogDescription>Create it empty, then add grants and give it to people.</DialogDescription>
        </DialogHeader>
        <form class="grid gap-4" @submit.prevent="create">
          <div class="grid gap-1.5">
            <Label for="r-label">Name</Label>
            <Input id="r-label" v-model="newLabel" placeholder="e.g. Production support" @blur="slugFromLabel" />
          </div>
          <div class="grid gap-1.5">
            <Label for="r-id">Id</Label>
            <Input id="r-id" v-model="newId" class="font-mono" placeholder="production-support" :aria-invalid="newId.length > 0 && !idValid" />
            <p class="text-xs text-muted-foreground">Lowercase letters, digits and dashes. Cannot be changed later.</p>
          </div>
          <div class="grid gap-1.5">
            <Label for="r-desc">Description</Label>
            <Textarea id="r-desc" v-model="newDescription" rows="2" placeholder="Who is this role for?" />
          </div>
          <FormError :error="error" />
          <DialogFooter>
            <Button type="button" variant="ghost" @click="open = false">{{ t.common.cancel }}</Button>
            <Button type="submit" :disabled="busy || !idValid || !newLabel.trim()">{{ t.common.create }}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  </div>
</template>
